import {EntityListColumn, EntityPage} from 'components';
import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import {
	ProviderThatEnablesGettingTooltipsFromContext,
	VkosTooltipTranslationProvider,
} from 'features/localizedTooltips';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Keyword, StandardPlusPcmsCluster, User, UserRole, Vko} from 'types';
import {} from './hooks/engineVariants.generated';
import {
	GetAllVkosDocument,
	useCreateVkoMutation,
	useDeleteVkoMutation,
	useGetAllVkosQuery,
	useUpdateVkoMutation,
} from './hooks/vko.generated';
import {mapToRef} from 'helpers';
import {Control} from 'react-hook-form';
import {renderArrayField} from 'components/EntityList/ColumnRenderers';
import {ARRAY_TYPE, PRIMITIVE_TYPE} from 'components/EntityList/EntityUtils';
import {ControlledTagPicker} from 'components/hookForms';
import {useGetAllMarketsQuery} from './hooks/markets.generated';
import {getUserDepartment, getVkoDetailsAll} from 'components/Personas';
import {useGetAllKeywordsQuery} from './hooks/keywords.generated';
import {useGetAllPcmsClustersQuery} from './hooks/pcmsClusters.generated';

const AdminVkoPage: React.FC = () => {
	const {t} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminVkosPage',
	});

	const {loading, data} = useGetAllVkosQuery();
	const {loading: loadingMarkets, data: dataMarkets} = useGetAllMarketsQuery();
	const {loading: keywordsLoading, data: keywordData} =
		useGetAllKeywordsQuery();
	const {loading: loadingPcmsClusters, data: dataPcmsClusters} =
		useGetAllPcmsClustersQuery();
	const [createVkoVariantMutation] = useCreateVkoMutation();
	const [updateVkoVariantMutation] = useUpdateVkoMutation();
	const [deleteVkoVariantMutation] = useDeleteVkoMutation();

	const vkos = React.useMemo(() => {
		const vkosSorted = (data?.vkos ?? []).slice().sort((a, b) => {
			const departA = getUserDepartment(a as User) ?? 'z';
			const departB = getUserDepartment(b as User) ?? 'z';
			return departA.localeCompare(departB);
		}) as Vko[];
		const keywords = (keywordData?.keywords ?? []) as Keyword[];
		const vkoDetailsAll = getVkoDetailsAll(keywords, vkosSorted);

		return vkosSorted.map(x => ({
			...x,
			keywords:
				vkoDetailsAll.find(y => y.userId === x.vkoUser.id)?.keywords ?? [],
		}));
	}, [data, keywordData]);

	console.log(vkos);
	const markets = React.useMemo(
		() => dataMarkets?.markets ?? [],
		[dataMarkets],
	);

	const pcmsClusters = React.useMemo(
		() => (dataPcmsClusters?.standardPlusPcmsClusters ?? []) as StandardPlusPcmsCluster[],
		[dataPcmsClusters],
	);

	const refetchQueries = [GetAllVkosDocument];

	const createVko = React.useCallback((vko: Vko) => {
		createVkoVariantMutation({
			variables: {
				input: {
					userRef: {id: vko.vkoUser.id},
					marketRefs: mapToRef(vko.markets),
					standardPlusPcmsClusterRefs: mapToRef(vko.standardPlusPcmsClusters),
				},
			},
			refetchQueries,
		});
	}, []);

	const updateVko = React.useCallback((vko: Vko) => {
		updateVkoVariantMutation({
			variables: {
				input: {
					id: vko.id,
					userRef: {id: vko.vkoUser.id},
					marketRefs: mapToRef(vko.markets),
					standardPlusPcmsClusterRefs: mapToRef(vko.standardPlusPcmsClusters),
				},
			},
			refetchQueries,
		});
	}, []);

	const deleteVko = React.useCallback((id: string) => {
		deleteVkoVariantMutation({
			variables: {
				input: {id},
			},
			refetchQueries,
		});
	}, []);

	const columns: EntityListColumn[] = React.useMemo(
		() => [
			{
				key: 'department',
				name: t('Department'),
				fieldName: 'department',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				getFieldValue: getUserDepartment,
				onRender: getUserDepartment,
				dataType: PRIMITIVE_TYPE,
			},
			{
				key: 'keywords',
				name: t('Keywords'),
				fieldName: 'keywords',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'markets',
				name: t('Markets'),
				fieldName: 'markets',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'standardPlusPcmsClusters',
				name: t('PcmsClusters'),
				fieldName: 'standardPlusPcmsClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
				sortable: true,
			},
		],
		[t],
	);

	const formElements = (control: Control<Vko>) => {
		return (
			<div>
				<ProviderThatEnablesGettingTooltipsFromContext>
					<ControlledTagPicker
						name={'markets'}
						control={control}
						label={t('Markets')}
						selectableItems={markets}
						getKey={item => item.id}
						getName={item => item.name}
					/>
					<ControlledTagPicker
						name={'standardPlusPcmsClusters'}
						control={control}
						label={t('PcmsClusters')}
						selectableItems={pcmsClusters}
						getKey={item => item.id}
						getName={item => item.name}
					/>
				</ProviderThatEnablesGettingTooltipsFromContext>
			</div>
		);
	};

	return (
		<ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
			<LoadWrapper loading={loading || loadingMarkets || keywordsLoading || loadingPcmsClusters}>
				<VkosTooltipTranslationProvider>
					<EntityPage
						items={vkos as any}
						entityDisplayName={t('Vko')}
						createEntity={createVko}
						updateEntity={updateVko}
						deleteEntity={deleteVko}
						additionalColumns={columns}
						renderAdditionalFormElements={control => formElements(control)}
						hideCreate={true}
						hideDelete={true}
					/>
				</VkosTooltipTranslationProvider>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

export default AdminVkoPage;
